import React, { useState } from 'react';
import { Button, CircleButton, FormTabSwitcher, PageHeader } from '@ui';
import useTranslation from '@app/hooks/use-translation';
import { navigationRoutes } from '@app/utils/navigation-routes';
import {
  useChangeMonitoringFormStatusMutation,
  useCloneMonitoringFormMutation,
  useDeleteMonitoringFormMutation,
  useGetMonitoringFormsBulkQuery,
} from '@app/store/api/qm-module/qm-manage/qm-manage-monitoring-forms.api';
import { MonitoringFormInfoType } from '@app/interfaces/qm-module/qm-manage/qm-monitoring-forms.type';
import AnalyticsStatus from '@app/components/ui/analytics-status/analytics-status';
import { TableItem } from '@app/components/rubber-table/table.types';
import { useNavigate } from 'react-router-dom';
import { RemovalModal } from '@app/components/removal-modal';

import MonitoringFormsTable from './blocks/monitoring-forms-table';

type TableTypes = 'chats' | 'voice';

const QmManageMonitoringForms = () => {
  const { t } = useTranslation('pages.qmManage.monitoring_forms');
  const navigate = useNavigate();
  // api
  const [deleteMF] = useDeleteMonitoringFormMutation();
  const [cloneMF] = useCloneMonitoringFormMutation();
  const [changeMfStatus] = useChangeMonitoringFormStatusMutation();
  const { data: monitoringFormsList } = useGetMonitoringFormsBulkQuery({});
  // api
  const [monitoringFormForDelete, setMonitoringFormForDelete] = useState<MonitoringFormInfoType>();
  const [activeMFTable, setActiveMFTable] = useState<TableTypes>('voice');
  const switcherOption = [
    { value: 'voice', title: t('records') },
    { value: 'chats', title: t('chats') },
  ];

  function handleCloneMonitoringForm(id: string) {
    cloneMF({ id })
      .unwrap()
      .then((value) => {
        navigate(`./${value.id}/`);
      });
  }

  function handleDeleteMonitoringForm() {
    if (!monitoringFormForDelete?.id) return;
    monitoringFormForDelete.status === 'ACTIVE'
      ? changeMfStatus({ id: monitoringFormForDelete.id, status: 'ARCHIVE' })
      : deleteMF({ id: monitoringFormForDelete.id });
  }

  function handleCloseRemovalModal() {
    setMonitoringFormForDelete(undefined);
  }
  // const downloadDropMenu: DropMenuItem[] = [
  //   { title: t('download_as_pdf'), key: 'pdf' },
  //   { title: t('download_as_xlsx'), key: 'xlsx' },
  // ];

  const reformatMFListForTable = (mforms: MonitoringFormInfoType[]): TableItem[] => {
    return mforms.map((form) => ({
      name: form.name,
      dt_add: form.dt_add,
      status: (
        <div className="w-fit">
          <AnalyticsStatus
            additionalStatusList={{
              ACTIVE: { title: t('status_active') },
              NEW: { title: t('status_new') },
            }}
            status={form.status}
          />
        </div>
      ),
      actions: (
        <div className="w-full flex items-center justify-center gap-[5px] ">
          <CircleButton
            hintTitle={t('hints.settings')}
            icon="SettingIcon"
            link={`./${form.id}/`}
            size={18}
            className="hover:text-action"
          />
          <CircleButton
            hintTitle={t('hints.copy')}
            icon="CopyIcon"
            size={18}
            className="hover:text-action"
            onClick={() => handleCloneMonitoringForm(form.id)}
          />

          <CircleButton
            hintTitle={t('hints.deleteHint')}
            icon="TrashIcon"
            size={18}
            className="text-basic_red"
            disable={form.status === 'ARCHIVE'}
            onClick={() => setMonitoringFormForDelete(form)}
          />
        </div>
      ),
    }));
  };

  return (
    <>
      <PageHeader
        customClassName="inline-flex items-center"
        childIsFull
        label={t('page_title')}
        icon="DocumentIcon"
      >
        <div className="flex items-center w-full  pl-[20px]">
          <div className="w-[40%]">
            <FormTabSwitcher
              classname="!bg-[#efefef]"
              valuesList={switcherOption}
              isActive={activeMFTable}
              onChange={(val) => setActiveMFTable(val as TableTypes)}
            />
          </div>
          <div className="flex items-center  gap-[15px] justify-end w-full">
            <Button
              link={navigationRoutes.newProject}
              icon="CirclePlusIcon"
              label={t('addFormButtonLabel')}
            />
          </div>
        </div>
      </PageHeader>

      <MonitoringFormsTable
        reformatMFListForTable={reformatMFListForTable}
        accordionsListData={monitoringFormsList?.[activeMFTable] || []}
      />
      <RemovalModal
        onClose={handleCloseRemovalModal}
        show={!!monitoringFormForDelete}
        handleDelete={handleDeleteMonitoringForm}
        description={
          monitoringFormForDelete?.status === 'ACTIVE'
            ? t('moveMFToArchive')
            : t('deleteMFDescription')
        }
      />
    </>
  );
};

export default QmManageMonitoringForms;
